import { JSX, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { GetServerSideProps } from 'next';
import NotFound from 'src/NotFound';
import Layout from 'src/Layout';
import {
  SitecoreContext,
  ComponentPropsContext,
  RouteData,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { handleEditorFastRefresh } from '@sitecore-jss/sitecore-jss-nextjs/utils';
import { SitecorePageProps } from 'lib/page-props';
import { sitecorePagePropsFactory } from 'lib/page-props-factory';
import { componentBuilder } from 'temp/componentBuilder';
import { ExtendedStyleguideSitecoreContextValue } from 'src/Layout';
import Head from 'next/head';
import { rawRequest, gql } from 'graphql-request';
import { getDeliveryUrl, getDeliveryKey } from 'lib/util';
import { ProviderMetaFields } from 'components/types/ProviderTypes';
import { TrackingProvider } from 'lib/tracking';
import sitecoreRedirect from 'lib/sc-redirect';

const SitecorePage = ({
  notFound,
  componentProps,
  layoutData,
  headLinks,
  provider,
  linksource,
}: SitecorePageProps & { provider: ProviderMetaFields; linksource: string | null }): JSX.Element => {
  const router = useRouter();
  const isRedirecting = useRef(false);
  useEffect(() => {
    if (router.isFallback) return;

    // Handle Experience Editor Fast Refresh
    handleEditorFastRefresh();
  }, [notFound, layoutData?.sitecore?.route]);

  if (!layoutData?.sitecore?.route && !isRedirecting.current) {
    return <NotFound />;
  }

  const isEditing = layoutData.sitecore.context.pageEditing;

  const contextValue: ExtendedStyleguideSitecoreContextValue = {
    sitecore: {
      context: {
        ...layoutData.sitecore.context,
        site: {
          name: layoutData.sitecore.context.site?.name ?? '',
        },
      },
      route:
        (layoutData.sitecore
          .route as ExtendedStyleguideSitecoreContextValue['sitecore']['route']) ||
        ({} as RouteData),
    },
    site: {
      name: layoutData.sitecore.context.site?.name ?? '',
    },
    itemPath: layoutData.sitecore.route?.itemId,
    itemId: layoutData?.sitecore.route?.itemId || undefined,
  };

  return (
    <TrackingProvider value={{ linksource }}>
      <>
        <Head>
          <title>
            {`${provider?.FullName?.value}, ${provider?.Degrees?.value} | ${
              provider?.Specialty?.value
                ? provider?.Specialty?.value
                    .split('|')
                    .map((item: string) => item.trim())[0]
                : ''
            }${
              provider?.Locations?.value &&
              provider.Locations.value.length > 0 &&
              provider.Locations.value[0]?.fields?.City?.value
                ? ` in ${provider.Locations.value[0].fields.City.value}`
                : ''
            }`}
          </title>
          <meta
            name="description"
            content={`Learn about ${provider.FullName?.value}, ${provider?.Degrees?.value}, who practices ${
              provider?.Specialty?.value
                ? provider?.Specialty?.value
                    .split('|')
                    .map((item: string) => item.trim())[0]
                : ''
            }${
              provider?.Locations?.value &&
              provider.Locations.value.length > 0 &&
              provider.Locations.value[0]?.fields?.LocationName?.value
                ? ` at ${provider.Locations.value[0].fields.LocationName.value} in ${provider.Locations.value[0].fields.City.value}, ${provider.Locations.value[0].fields.State.value}`
                : ''
            }. Schedule an appointment today.`}
          />
          <meta name="kyruus-id" content={provider?.ExternalId?.value} />
          <meta
            name="provider-name"
            content={`${provider?.FullName?.value}, ${provider?.Degrees?.value}`}
          />
          <meta
            property="twitter:image"
            content={
              provider?.ProviderImage?.value?.value &&
              typeof provider.ProviderImage.value.value === 'object' &&
              'src' in provider.ProviderImage.value.value
                ? (provider.ProviderImage.value.value as { src?: string }).src ||
                  ''
                : ''
            }
          />
          <meta
            name="twitter:title"
            content={`${provider?.FullName?.value}, ${provider?.Degrees?.value} | ${
              provider?.Specialty?.value
                ? provider?.Specialty?.value
                    .split('|')
                    .map((item: string) => item.trim())[0]
                : ''
            }${
              provider?.Locations?.value &&
              provider.Locations.value.length > 0 &&
              provider.Locations.value[0]?.fields?.City?.value
                ? ` in ${provider.Locations.value[0].fields.City.value}`
                : ''
            }`}
          />
          <meta
            name="twitter:description"
            content={`Learn about ${provider.FullName?.value}, ${provider?.Degrees?.value}, who practices ${
              provider?.Specialty?.value
                ? provider?.Specialty?.value
                    .split('|')
                    .map((item: string) => item.trim())[0]
                : ''
            }${
              provider?.Locations?.value &&
              provider.Locations.value.length > 0 &&
              provider.Locations.value[0]?.fields?.LocationName?.value
                ? ` at ${provider.Locations.value[0].fields.LocationName.value} in ${provider.Locations.value[0].fields.City.value}, ${provider.Locations.value[0].fields.State.value}`
                : ''
            }. Schedule an appointment today.`}
          />
          <meta
            property="og:image"
            content={
              provider?.ProviderImage?.value?.value &&
              typeof provider.ProviderImage.value.value === 'object' &&
              'src' in provider.ProviderImage.value.value
                ? (provider.ProviderImage.value.value as { src?: string }).src ||
                  ''
                : ''
            }
          />
          <meta
            property="og:title"
            content={`${provider?.FullName?.value}, ${provider?.Degrees?.value} | ${
              provider?.Specialty?.value
                ? provider?.Specialty?.value
                    .split('|')
                    .map((item: string) => item.trim())[0]
                : ''
            }${
              provider?.Locations?.value &&
              provider.Locations.value.length > 0 &&
              provider.Locations.value[0]?.fields?.City?.value
                ? ` in ${provider.Locations.value[0].fields.City.value}`
                : ''
            }`}
          />
          <meta
            property="og:description"
            content={`Learn about ${provider.FullName?.value}, ${provider?.Degrees?.value}, who practices ${
              provider?.Specialty?.value
                ? provider?.Specialty?.value
                    .split('|')
                    .map((item: string) => item.trim())[0]
                : ''
            }${
              provider?.Locations?.value &&
              provider.Locations.value.length > 0 &&
              provider.Locations.value[0]?.fields?.LocationName?.value
                ? ` at ${provider.Locations.value[0].fields.LocationName.value} in ${provider.Locations.value[0].fields.City.value}, ${provider.Locations.value[0].fields.State.value}`
                : ''
            }. Schedule an appointment today.`}
          />
          {/* <meta
            property="og:url"
            content={route.fields && route.fields.OpenGraphSiteName?.value}
          />*/}
        </Head>
        <ComponentPropsContext value={componentProps}>
          <SitecoreContext
            componentFactory={componentBuilder.getComponentFactory({
              isEditing,
            })}
            layoutData={layoutData}
          >
            <Layout
              context={contextValue}
              layoutData={layoutData}
              headLinks={headLinks}
              schemaText={provider?.SchemaText}
            />
          </SitecoreContext>
        </ComponentPropsContext>
      </>
    </TrackingProvider>
  );
};

export const getServerSideProps: GetServerSideProps = async context => {
  const linksource = context.req.cookies['linksource'] ?? null;
  if (context.params?.path) {
    let sourceUrl = '/providers';
    if (Array.isArray(context.params.path)) {
        sourceUrl += '/' + context.params.path.join('/');
    } else {
        sourceUrl += '/' + context.params.path;
    }

    const redirect = await sitecoreRedirect(sourceUrl);
    if (redirect) {
        return {
            redirect: {
                destination: redirect.destination,
                statusCode: redirect.permanent ? 301 : 302,
            },
        };
    }
  }
  let provider;
  if (context.params) {
    if (context.params.path) {
      const deliveryKey = getDeliveryKey();
      const endpoint = getDeliveryUrl();
      const queryPath = context.params?.path as Array<string>;
      const providerItemName = queryPath?.length
        ? queryPath[queryPath.length - 1]
        : queryPath[1];
      const providerId = providerItemName?.substring(
        providerItemName.lastIndexOf('-') + 1,
        providerItemName.length
      );
      const providerQuery = gql`
        query ($externalID: String!) {
          search(
            where: {
              AND: [
                {
                  name: "_path"
                  value: "{6A68FA22-BE0C-46BB-912C-22F1F83E28DC}"
                  operator: CONTAINS
                }
                { name: "ExternalID", value: $externalID, operator: EQ }
              ]
            }
            first: 1
          ) {
            results {
              Degrees: field(name: "Degrees") {
                value: value
              }
              FullName: field(name: "FullName") {
                value
              }
              Locations: field(name: "Locations") {
                value: jsonValue
              }
              Specialty: field(name: "Specialty") {
                value: value
              }
              ExternalId: field(name: "ExternalId") {
                value
              }
              ProviderImage: field(name: "ProviderImage") {
                value: jsonValue
              }
              SchemaText: field(name: "SchemaText") {
                value
              }
            }
          }
        }
      `;
      const variables = { externalID: providerId };
      const headers = {
        sc_apikey: deliveryKey,
      };

      const getProvider = async () => {
        try {
          const { data } = await rawRequest(
            endpoint,
            providerQuery,
            variables,
            headers
          );
          return data.search.results[0];
        } catch (error) {
          console.error(error);
        }
      };

      provider = await getProvider();
    }

    context.params.requestPath = context.params.path;
    context.params.path = [`providers/,-w-,`];
  }
  const props = await sitecorePagePropsFactory.create(context);

  return {
    props: { ...props, provider: provider, linksource },
    notFound: props.notFound,
  };
};

export default SitecorePage;
